import '../../gs-core/common/util/array.prototype';

import {
  ActiveStatuses, Organization, Permission, Person, User, UserModel, ValidationError
} from '../../gs-core/common';

import { ActionType, ActionTypes, EDocsPermission, EDocsPermissions } from './EDocsPermission';
import { ViewAccess, ViewAccessTypes } from './ViewAccess';
import { EDocsRoles } from './EDocsRoles';
import { Subject } from './Subject';
import { SearchScope } from './SearchScope';

const ADMIN_ACCESS = EDocsPermissions.ADMIN_ACCESS;
const EDIT_DOCUMENTS = EDocsPermissions.EDIT_DOCUMENTS;
const ASSIGN_TO_PROGRAM = EDocsPermissions.ASSIGN_TO_PROGRAM;
const SUBJECTS_GROUPS = ViewAccessTypes.SUBJECTS_GROUPS;
const SUBJECTS_GROUPS_AND_SUBJECT = ViewAccessTypes.SUBJECTS_GROUPS_AND_SUBJECT;

export interface DocProxy {
  subject?: Subject;
  owningUnit?: Organization;
  viewAccess?: ViewAccess;
}

export class PermissionEvaluator {
  static forUser(currentUser: User) {
    if (!currentUser.isA) {
      throw new Error('current user must be a UserModel');
    }

    return new PermissionEvaluator(currentUser as UserModel);
  }

  private constructor(
    private currentUser: UserModel,
    private permissions: Permission[] = [],
    private doc?: DocProxy
  ) { }

  withPermissions(permissions: Permission[]): PermissionEvaluator {
    return new PermissionEvaluator(this.currentUser, permissions, this.doc);
  }

  findSearchScope(actions: EDocsPermission[]): SearchScope {
    const currentUser = this.currentUser as UserModel;

    const roleMap = actions
      .reduce(
        (map, action) => {
          const permissions = this.permissions.filter(permission => action === permission.code);

          const scopeGroups: Organization[] = permissions
            .filter(permission => !!permission.scopeGroup)
            .map(permission => permission.scopeGroup) as Organization[];

          let usersGroups = currentUser.roles
            .filter(role =>
              permissions.some(permission =>
                !permission.scopeGroup && currentUser.isA(role.code).of(permission.group)
              )
            )
            .map(role =>
              // If mentor role, return the program for that mentorship otherwise return the group
              role.targetMustBeInGroup || role.group
            ) as Organization[];

          // If action is ASSIGN_TO_PROGRAM, the search scope should be the college not the program itself
          if (action === ASSIGN_TO_PROGRAM) {
            usersGroups = usersGroups.flatMap(program => {
              if (!program.isOfType || !program.isOfType('Program') || !program.findRoles) {
                return [ program ];
              }

              return program
                .findRoles(EDocsRoles.SUBUNIT)
                .map(subunit => subunit.group as Organization);
            });
          }

          const assignRole = EDocsPermissions.toRole(action);
          const groups = [...scopeGroups, ...usersGroups].unique();

          if (groups.length) {
            map[assignRole.code] = groups;
          }

          return map;
        },
        {} as { [role: string]: Organization[] }
      );

    const userTypes = Object.keys(roleMap).map(role => EDocsRoles.toUserType(role)).unique();

    return { roleMap, userTypes, statuses: ActiveStatuses.ALL };
  }

  findOwningUnits(): Organization[] {
    return this.currentUser.roles
      .map(role => role.group)
      .flatMap(currentUsersGroup => {
        return this.permissions
          .filter(permission => permission.code === EDIT_DOCUMENTS)
          .map(permission => {
            if (!currentUsersGroup.isUnder) {
              throw new Error(`Group ${currentUsersGroup.code} must be a UserModel`);
            }

            if (
              currentUsersGroup.isUnder(permission.group) &&
              this.currentUser.isA(permission.role).of(permission.group)
            ) {
              return currentUsersGroup;
            }

            return null;
          })
          .filter(group => !!group) as Organization[];
      })
      .unique()
      .sort(UserModel.sort) as Organization[];
  }

  findScopeGroups(actionType: ActionType): Organization[] {
    return this.permissions
      .filter(permission => permission.code.startsWith(actionType) && !!permission.scopeGroup)
      .map(permission => permission.scopeGroup)
      .unique() as Organization[];
  }

  findOwningUnitsOverlap(): Organization[] {
    if (!this.doc || !this.doc.subject) {
      throw new Error('Missing doc or doc subject');
    }

    const owningUnit = this.doc.owningUnit && this.doc.owningUnit.isUnder ? this.doc.owningUnit as UserModel : null;
    const actions = owningUnit ?
      [ EDIT_DOCUMENTS ] :
      EDocsPermissions.fromAction(ActionTypes.ASSIGN, this.doc.subject.type);

    return this.doc.subject.groups
      .flatMap(subjectsGroup => {
        return this.permissions
          .filter(permission => actions.includes(permission.code as EDocsPermission))
          .map(permission => {
            if (!subjectsGroup.isUnder) {
              throw new Error(`Subject group ${subjectsGroup.code} must be a UserModel`);
            }

            if (
              subjectsGroup.isUnder(permission.scopeGroup) ||
              ( owningUnit && owningUnit.isUnder(permission.scopeGroup) )
            ) {
              return permission.group;
            }

            if (
              subjectsGroup.isUnder(permission.group) &&
              this.currentUser.isA(permission.role).of(subjectsGroup)
            ) {
              return subjectsGroup;
            }

            return null;
          })
          .filter(group => !!group) as User[];
      })
      .unique()
      .sort(UserModel.sort) as Organization[];
  }

  forDoc(doc: DocProxy): PermissionEvaluator {
    return new PermissionEvaluator(this.currentUser, this.permissions, doc);
  }

  canView(): boolean {
    if (!this.currentUser || !this.doc || !this.doc.owningUnit) {
      throw new ValidationError(`Missing current user or document`);
    }

    if (this.permissions.some(permission => permission.code === ADMIN_ACCESS)) {
      return true;
    }

    const currentUser = this.currentUser as UserModel;
    const doc = this.doc;
    const subject = this.doc.subject;
    const subjectsGroups = ((subject && subject.groups) || []) as UserModel[];
    const subjectType = subject && subject.type;
    const owningUnit = this.doc.owningUnit as UserModel;
    const viewActions = EDocsPermissions.fromAction(ActionTypes.VIEW, subjectType);

    // TODO: add in student access later
    // if (doc.viewAccess === SUBJECTS_GROUPS_AND_SUBJECT && subject && currentUser.id === subject.user.id) {
    //   return true;
    // }

    return this.permissions
      .filter(permission => viewActions.includes(permission.code as EDocsPermission))
      .some(permission => {
        if (
          // Allow dean's office, admin, testers, case managers to view any doc under any grad program
          owningUnit.isUnder(permission.scopeGroup as Organization) ||

          // Allow dean's office, admin, testers, case managers to view any doc under any grad program
          subjectsGroups.some(subjectsGroup => subjectsGroup.isUnder(permission.scopeGroup as Organization)) ||

          // Allow program coordinators, faculty advisors, chairs ... in owning unit
          (
            currentUser.isA(permission.role).of(owningUnit) &&
            owningUnit.isUnder(permission.group) &&
            !permission.role.code.endsWith('MENTOR')        // Exclude mentors since they are handled next
          ) ||

          // Allow mentors if they are mentoring the student in the owning unit
          (subject && currentUser.isA(permission.role).forMentee(subject.user as Person).ofAny([owningUnit]))
        ) {
          return true;
        }

        // If the doc is NOT shared STOP
        if (doc.viewAccess !== SUBJECTS_GROUPS && doc.viewAccess !== SUBJECTS_GROUPS_AND_SUBJECT) {
          return false;
        }

        // Allow program coordinators, faculty advisors, chairs ... if they are in one of the student's programs
        return (
            subjectsGroups.some(subjectsGroup =>
              currentUser.isA(permission.role).of(subjectsGroup) &&
              subjectsGroup.isUnder(permission.group)
            ) &&
            !permission.role.code.endsWith('MENTOR')        // Exclude mentors since they are handled next
          ) ||

          // Allow mentors if they are mentoring the student in any of the student's programs
          (!!subject && currentUser.isA(permission.role).forMentee(subject.user as Person).ofAny(subjectsGroups))
          ;
      });
  }

  canEdit(): boolean {
    if (!this.currentUser || !this.doc) {
      throw new ValidationError(`Missing current user or document`);
    }

    const currentUser = this.currentUser as UserModel;
    const subject = this.doc.subject;
    const subjectsGroups = ((subject && subject.groups) || []) as UserModel[];
    const owningUnit = this.doc.owningUnit as UserModel;
    const assignActions = EDocsPermissions.fromAction(ActionTypes.ASSIGN, subject && subject.type);

    const canEdit = this.permissions
      .filter(permission => permission.code === EDIT_DOCUMENTS)
      .some(permission =>
        // Allow dean's office to edit any doc owned by a dean's office unit
        owningUnit.isUnder(permission.scopeGroup as Organization) ||
        currentUser.isA(permission.role).of(owningUnit)
      );

    const canAssign = this.permissions
      .filter(permission => assignActions.includes(permission.code as EDocsPermission))
      .some(permission =>
        !subject ||

        // Dean's office can assign to any program under Graduate School
        subjectsGroups.some(subjectsGroup => subjectsGroup.isUnder(permission.scopeGroup)) ||

        subjectsGroups.some(subjectsGroup =>
          // Coordinator must be in the subject's group
          currentUser.isA(permission.role).of(subjectsGroup) &&

          // Subject's group must be part of the permission
          subjectsGroup.isUnder(permission.group)
        )
      );

    return canEdit && canAssign;
  }
}



// WEBPACK FOOTER //
// ./src/common/models/PermissionEvaluator.ts